<template>
  <div class="import-reports-show-view">
    <loader v-if="isLoading" />
    <template v-else>
      <h1>{{importerName}}</h1>

      <h3>{{$t('headlines.information')}}</h3>

      <attribute-table
        :data="importReport"
        :fields="['status', 'scope', 'startedAt', 'updatedAt', 'finishedAt', 'timingInfo', 'details' ]"
      >
        <template scope="statusProps" slot="status">
          <import-status
            :errors="statusProps.row.errorsCount"
            :value="statusProps.value"
          />
          <confirmation-button
            v-if="statusProps.value == 'RUNNING'"
            text="Cancel"
            @confirm="cancelImport()"
          />
        </template>
        <template scope="scopeProps" slot="scope">
          <span v-if="scopeProps.value">
            {{scopeProps.value}}
          </span>
          <text-placeholder v-else />
        </template>
        <template scope="startedAtProps" slot="startedAt">
        <span v-if="startedAtProps.value">
           {{startedAtProps.value | niceTime}}
         </span>
          <text-placeholder v-else />
        </template>
        <template scope="updatedAtProps" slot="updatedAt">
        <span v-if="updatedAtProps.value">
           {{updatedAtProps.value | niceTime}}
         </span>
          <text-placeholder v-else />
        </template>
        <template scope="finishedAtProps" slot="finishedAt">
        <span v-if="finishedAtProps.value">
           {{finishedAtProps.value | niceTime}}
         </span>
          <text-placeholder v-else />
        </template>
        <template scope="timingInfoProps" slot="timingInfo">
          <ul v-if="importReport.timingInfo">
            <li v-for="timingInfo in importReport.timingInfo">{{timingInfo}}</li>
          </ul>
          <text-placeholder v-else />
        </template>
        <template scope="detailsProps" slot="details">
          <table>
            <tr>
              <th>Schlüssel</th>
              <th>Wert</th>
              <th></th>
            </tr>
            <import-report-detail :keyPair="detail.key" :valuePair="detail.value" v-for="detail in importReport.details" :key="detail.key"/>
          </table>
        </template>
      </attribute-table>

      <template v-if="importReport.error">
        <h3>{{$t('headlines.errors')}}</h3>

        <modal
          :title="$t('headlines.error')"
          @hidden="onErrorModalHidden"
          auto-overflow
          class="uk-modal-container"
          ref="errorModal"
          v-if="viewedError"
        >
          <h3>{{viewedError.message}}</h3>

          <dl v-if="viewedError.details.length">
            <template v-for="errorDetail in viewedError.details">
              <dt>{{errorDetail.key}}</dt>
              <dd>
                <pre>{{errorDetail.value}}</pre>
              </dd>
            </template>
          </dl>
        </modal>

        <table class="uk-table uk-table-divider uk-table-justify">
          <thead>
            <tr>
              <th>Fehlerart</th>
              <th>Import-Schritt</th>
              <th>Artikelnummer</th>
              <th>{{$t('fields.message')}}</th>
              <th>{{$t('fields.occurredAt')}}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="error in importReport.errors.entries">
              <td>{{error.type}}</td>
              <td>{{error.step}}</td>
              <td>
                <a :href="'/products?search=' + error.article_number  ">
                  {{error.article_number}}
                </a>
              </td>
              <td>
                <a
                  @click.prevent="showError(error)"
                  href="#"
                >{{error.message | truncate(80)}}</a>
              </td>
              <td>
                <timeago :auto-update="60" :since="error.occurredAt" />
              </td>
            </tr>
          </tbody>
        </table>

        <paginator :page-info="importReport.errors.pagination" />
      </template>
    </template>
  </div>
</template>

<script>
import { parseInt } from 'lodash';
import { mapGetters } from 'vuex';
import AttributeTable from '@/components/AttributeTable';
import ConfirmationButton from '@/components/ConfirmationButton';
import importReportQuery from '@/api/queries/import-report.graphql';
import ImportStatus from '@/components/ImportStatus';
import Modal from '@/components/Modal';
import Paginator from '@/components/Paginator';
import TextPlaceholder from '@/components/TextPlaceholder';
import View from '@/mixins/View';
import CancelRunningImportMutation
  from '@/api/mutations/cancel-running-import.graphql';
import Toast from '@/helpers/Toast';
import ImportReportDetail from './ImportReportDetail';

export default {
  name: 'import-reports-show-view',
  mixins: [View],
  components: {
    AttributeTable,
    ConfirmationButton,
    ImportStatus,
    Modal,
    Paginator,
    TextPlaceholder,
    ImportReportDetail,
  },
  data() {
    return { viewedError: null };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    breadcrumbs() {
      return [
        {
          key: 'show',
          text: () => this.importerName,
          route: { name: 'import-report', params: { id: this.id } },
          isLoading: !this.isSuccessfullyLoaded,
        },
      ];
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.importReport;
    },
    importerName() {
      if (!this.isSuccessfullyLoaded) return undefined;
      return this.$t(`importers.${this.importReport.importer}`);
    },
    errorPage() {
      const page = parseInt(this.$route.query.page);
      if (isNaN(page)) return 1;
      return page;
    },
    ...mapGetters(['currentUser']),
  },
  apollo: {
    importReport: {
      query: importReportQuery,
      variables() {
        return {
          id: this.id,
          errorPagination: { page: this.errorPage, size: 25 },
        };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      result({ data }) {
        const importIsFinished =
          data &&
            data.importReport &&
            data.importReport.status === 'FINISHED';

        const query = this.$apollo.queries.importReport;
        if (importIsFinished) {
          query.stopPolling();
        } else {
          query.startPolling(5000);
        }
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  methods: {
    async showError(error) {
      this.viewedError = error;
      await this.$nextTick();
      this.$refs.errorModal.show();
    },
    onErrorModalHidden() {
      this.viewedError = null;
    },
    async cancelImport() {
      await this.$apollo.mutate({
        mutation: CancelRunningImportMutation,
        variables: {
          id: this.id,
          // FIXME: Someone with vue Skill should add a text input
          // to the ConfirmationButton so the user MUST type a reason
          reason: 'none given',
          user: this.currentUser.name,
        },
      });
      Toast.success('Import has been cancelled.');
    },
  },
  i18n: {
    messages: {
      de: {
        fields: {
          occurredAt: 'Aufgetreten',
          message: 'Nachricht',
          scope: 'Umfang',
        },
        headlines: {
          error: 'Fehler',
          errors: 'Fehlerdetails',
          information: 'Informationen',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// ShowView.vue?5f0f084f